import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Button } from './Button';
import { Modal } from './Modal';

// ConfirmDialog - the app's confirmation dialog (V2-016; DESIGN.md CONF-01, DLG-01, DLG-05, DLG-10).
//
// Replaces the browser's confirm(), which looks foreign, blocks the page and cannot be styled.
// The title is the question ("Delete Role?"), the button names the action ("Delete"), Cancel is
// always there and outlined, and a destructive action's button is red. Any explanation is an
// ordinary sentence in the body.
//
// Two ways to use it:
//   - <ConfirmDialog show … /> when the caller already owns the open/closed state;
//   - `await confirmAction({ … })` in place of `window.confirm(…)`: resolves true on the action
//     button, false on Cancel / Escape / close. It mounts its own dialog, so it needs no provider.

export interface ConfirmDialogProps {
  show: boolean;
  /** The question, in Title Case: "Delete Role?" */
  title: string;
  /** What happens, as normal sentences. Optional. */
  children?: React.ReactNode;
  /** Names the action: "Delete", "Withdraw", "Approve". */
  confirmLabel: string;
  cancelLabel?: string;
  /** Red action button for anything that removes or loses something. */
  destructive?: boolean;
  /** Overrides the action button colour when it is neither the default nor destructive. */
  confirmVariant?: 'primary' | 'danger' | 'success';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  show,
  title,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  confirmVariant,
  busy = false,
  onConfirm,
  onCancel,
}) => (
  <Modal show={show} onHide={busy ? () => undefined : onCancel} size="sm" backdrop="static">
    <Modal.Header closeButton={!busy}>
      <Modal.Title>{title}</Modal.Title>
    </Modal.Header>
    {children ? <Modal.Body>{children}</Modal.Body> : null}
    <Modal.Footer>
      <Button type="button" variant="outline-secondary" onClick={onCancel} disabled={busy}>
        {cancelLabel}
      </Button>
      <Button
        type="button"
        variant={confirmVariant ?? (destructive ? 'danger' : 'primary')}
        onClick={onConfirm}
        disabled={busy}
      >
        {confirmLabel}
      </Button>
    </Modal.Footer>
  </Modal>
);

export interface ConfirmActionOptions {
  title: string;
  body?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  confirmVariant?: ConfirmDialogProps['confirmVariant'];
}

const Host: React.FC<{ options: ConfirmActionOptions; settle: (ok: boolean) => void }> = ({ options, settle }) => {
  const [show, setShow] = useState(true);
  const close = (ok: boolean) => {
    setShow(false);
    settle(ok);
  };
  return (
    <ConfirmDialog
      show={show}
      title={options.title}
      confirmLabel={options.confirmLabel}
      cancelLabel={options.cancelLabel}
      destructive={options.destructive}
      confirmVariant={options.confirmVariant}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    >
      {options.body}
    </ConfirmDialog>
  );
};

/** Promise-based replacement for `window.confirm`. Resolves true only on the action button. */
export function confirmAction(options: ConfirmActionOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);
    let settled = false;
    const settle = (ok: boolean) => {
      if (settled) return;
      settled = true;
      resolve(ok);
      // Let the close render, then remove the dialog.
      setTimeout(() => {
        root.unmount();
        container.remove();
      }, 0);
    };
    root.render(<Host options={options} settle={settle} />);
  });
}

export default ConfirmDialog;
